import styles from './AllProducts.module.less'
import React, { useState, useEffect, useRef } from 'react'
import { useCartStore, useCorpInfoStore, useCorpSettingStore } from '@/stores'
import { utils, apis, productUtil } from '@/utils'
import ProductCell from '../ProductCell/ProductCell'
import Masonry from 'react-masonry-css'
import { InfiniteScroll } from 'antd-mobile' // List
import useRefState from '@/hooks/useRefState'
import type { RequestParams } from '@/types/product'
import { useMount, useUpdateEffect } from 'ahooks'
import { t } from '@/utils/i18n'
import shoppingBag from './shopping-bag.svg'

const { to, px2rem, cloneDeep } = utils
const { productApi } = apis

const Products: React.FC = () => {
  const { corpInfo } = useCorpInfoStore()
  const { cart } = useCartStore()
  const { corpSetting } = useCorpSettingStore()
  const { corpId, customerId } = corpInfo
  const { cartId } = cart
  const [productList, setProductList] = useState([])
  const [cellWidth, setCellWidth] = useState('')
  const refMasonry = useRef<HTMLDivElement | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [pageNum, setPageNum, pageNumRef] = useRefState(1)
  // 商品列表搜索分类/推荐相关参数
  const [filter, setFilter] = useState<RequestParams>({
    otherType: 0, // 买过 传1
    productStatus: 0, // 推荐传2
    recommendType: 23, // 推荐时传 推荐类型：1新品 2爆品 4促销 23一周销量倒序 99多货品分享
    categoryId: '0',
    categoryLevel: 0,
    combineType: 0,
    customerId: customerId || '0',
    interfaceVersion: 1,
    onScan: false,
    sortDirection: '1',
    sortKey: '5',
    unionMall: 0,
  })

  const breakpointColumnsObj = {
    default: 2,
  }

  async function getProductList() {
    const params = {
      corpId,
      cartId,
      pageSize: 20,
      pageNum: pageNumRef.current,
      ...filter,
    }
    const [error, res] = await to(productApi.prodList(params))

    if (error) return

    const { data: list, code } = res

    if (pageNumRef.current === 1) {
      setProductList(list || [])
    } else {
      setProductList(productList.concat(...(list || [])))
    }

    // 最多显示100条；即前 5 页数据
    if (
      (Array.isArray(list) && pageNumRef.current >= 5) ||
      error ||
      (code === 0 && !list)
    ) {
      setHasMore(false)
    } else {
      setHasMore(true)
    }
  }

  async function loadMore() {
    setPageNum(pageNum + 1)
    await getProductList()
  }

  useEffect(() => {
    if (refMasonry.current) {
      const offset = window.getComputedStyle(refMasonry.current)

      setCellWidth(
        `calc(${window.screen.width / 2}px - ${offset.paddingLeft} - ${px2rem(
          8,
        )})`,
      )
    }
  }, [])

  async function init() {
    const sortOptions = productUtil.initSortOptions(
      corpSetting.cloudProductSort,
      corpSetting.salesOn,
      corpSetting.salesSlipOn,
    ) // 初始化排序选项
    const cloneFilter = cloneDeep(filter)
    cloneFilter.sortKey = sortOptions.sortActive.activeKey as string
    cloneFilter.sortDirection =
      sortOptions.sortActive.sortDirection === 'ASC' ? '0' : '1'

    setFilter(cloneFilter)
  }

  useMount(async () => {
    await init()
  })

  useUpdateEffect(() => {
    getProductList()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter])

  return (
    <div className={styles['product-wrap']}>
      <div className={styles['product-hd']}>
        <img className={styles['product-title-icon']} src={shoppingBag} />

        <h2 className={styles['product-title'] + ' ep-2'}>{t('推荐货品')}</h2>
      </div>

      <div className={styles['product-bd']} ref={refMasonry}>
        <Masonry
          breakpointCols={breakpointColumnsObj}
          className={styles['my-masonry-grid']}
          columnClassName={styles['my-masonry-grid_column']}
        >
          {productList.map((item, index) => (
            <ProductCell
              item={item}
              index={index}
              key={index}
              cellWidth={cellWidth}
            />
          ))}
        </Masonry>
        {hasMore && (
          <InfiniteScroll
            threshold={350}
            loadMore={loadMore}
            hasMore={hasMore}
          />
        )}
      </div>
    </div>
  )
}

export default Products
